import type { ReactElement, ReactNode } from 'react'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import Heading from 'LuxKit/Typography/Heading'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import type { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import { asArray } from 'lib/array/arrayUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

const LabelsContainer = styled(LabelGroup)`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
`

const CancellationWrapper = styled.div`
  width: fit-content;
`

interface Props {
  title: string
  dates?: string | false
  packageTitle?: string | Array<string>
  metaData?: ReactElement | false
  occupancy?: string | false
  cancellationPolicy?: ReactNode
  label?: ReactElement
  loyaltyPointsEarningConfiguration?: LoyaltyPointsEarningConfiguration
  hideAvailabilityDetails?: boolean
  hideTitleAndDates?: boolean
  roomName?: string
  comparisonCheckbox?: ReactElement
}

function CartReviewTileContent(props: Props) {
  const {
    title,
    dates,
    packageTitle,
    metaData,
    occupancy,
    cancellationPolicy,
    label,
    loyaltyPointsEarningConfiguration,
    hideAvailabilityDetails = false,
    hideTitleAndDates = false,
    roomName,
    comparisonCheckbox,
  } = props

  const packageTitles = useMemo(() => asArray(packageTitle), [packageTitle])

  return (
    <Group direction="vertical" gap={12}>
      {comparisonCheckbox}
      {label && <LabelsContainer>{label}</LabelsContainer>}
      {!hideTitleAndDates && <Group direction="vertical" gap={4}>
        <Heading variant="heading6">{title}</Heading>
        <BodyText variant="medium" colour="neutral-two">{dates}</BodyText>
      </Group>}
      <Group direction="vertical" gap={2}>
        {roomName && <BodyText variant="medium" colour="neutral-two">{roomName}</BodyText>}
        {!roomName && packageTitles.map((packageTitle) => <BodyText
          key={packageTitle}
          variant="medium"
          colour="neutral-two"
          lineClamp={5}
        >
          {packageTitle}
        </BodyText>)}
        {metaData}
        {!!occupancy && <BodyText variant="medium" colour="neutral-two">{occupancy}</BodyText>}
        <VerticalSpacer gap={12}>
          {!hideAvailabilityDetails && <CancellationWrapper>
            {cancellationPolicy}
          </CancellationWrapper>}
          {!hideAvailabilityDetails && loyaltyPointsEarningConfiguration &&
            <LoyaltyPointsEarnWithBreakdownModal
              calculationRequests={loyaltyPointsEarningConfiguration.calculationRequests}
              calculationType={loyaltyPointsEarningConfiguration.calculationType}
              hideStatusCredits={loyaltyPointsEarningConfiguration.hideStatusCredits}
            />}
        </VerticalSpacer>
      </Group>
    </Group>
  )
}

export default CartReviewTileContent
